import React, { useState, Children, cloneElement, isValidElement, useCallback } from 'react';
import StudioNavbar from './StudioNavbar';
import VaultDrawer from './VaultDrawer';

/**
 * StudioLayout - The main shell for the Creative Studio
 * Implements the "Pixar Studio" aesthetic with clear outlines and "pop-out" UI.
 * 
 * Modular Design: 
 * - Handles the outer frame (Navbar, Background, Vault)
 * - Renders specific 'Phase Canvases' as children
 * - Manages dropped artifacts state for drag-and-drop from vault
 */
const StudioLayout = ({ children, activePhase, onPhaseChange, user, episode, db, onNavigateBack }) => {
    const [isVaultOpen, setIsVaultOpen] = useState(false);
    const [droppedArtifacts, setDroppedArtifacts] = useState([]);

    // Function to open vault (can be passed to children)
    const handleOpenVault = () => {
        setIsVaultOpen(true);
    };

    /**
     * Handle artifact drop from vault to canvas
     * Updates the application state to render the artifact in its new position
     */
    const handleArtifactDrop = useCallback((artifact) => {
        setDroppedArtifacts(prev => [...prev, artifact]);
    }, []);

    /**
     * Remove an artifact from the canvas
     */
    const handleRemoveArtifact = useCallback((artifactId) => {
        setDroppedArtifacts(prev => prev.filter(a => a.id !== artifactId));
    }, []);

    /**
     * Clear all artifacts from the canvas
     */
    const handleClearArtifacts = useCallback(() => {
        setDroppedArtifacts([]);
    }, []);

    // Clone children to pass onOpenVault prop to each valid element
    const childrenWithProps = Children.map(children, (child) => {
        if (isValidElement(child)) {
            // Check if child is a div wrapper (like the phase wrappers) and clone its children too
            if (child.props.children && isValidElement(child.props.children)) {
                const innerChild = child.props.children;
                return cloneElement(child, {
                    children: cloneElement(innerChild, {
                        onOpenVault: handleOpenVault,
                        droppedArtifacts,
                        onArtifactDrop: handleArtifactDrop,
                        onRemoveArtifact: handleRemoveArtifact,
                        onClearArtifacts: handleClearArtifacts,
                        // Explicitly exclude ref from being passed
                        ref: undefined
                    })
                });
            }
            return cloneElement(child, {
                onOpenVault: handleOpenVault,
                droppedArtifacts,
                onArtifactDrop: handleArtifactDrop,
                onRemoveArtifact: handleRemoveArtifact,
                onClearArtifacts: handleClearArtifacts,
                ref: undefined
            });
        }
        return child;
    });

    return (
        <div className="h-screen w-full bg-[#FAFAFC] overflow-hidden flex flex-col font-sans text-slate-900 relative">
            {/* Background Ambience (Subtle & Clean) */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-gradient-to-tr from-indigo-50/40 to-purple-50/40 rounded-full blur-[100px] opacity-60"></div>
                <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-gradient-to-tr from-blue-50/40 to-teal-50/40 rounded-full blur-[100px] opacity-60"></div>
            </div>

            {/* Top Navigation */}
            <StudioNavbar
                activePhase={activePhase}
                onPhaseChange={onPhaseChange}
                onToggleVault={() => setIsVaultOpen(true)}
                onNavigateBack={onNavigateBack}
                episode={episode}
            />

            {/* Main Viewport */}
            <main className="flex-1 relative z-10 overflow-hidden p-3 sm:p-4 lg:p-6 pt-0">
                {/* The "Stage" - Where the magic happens */}
                <div className="w-full h-full bg-white/60 backdrop-blur-md rounded-[24px] sm:rounded-[32px] border-2 border-slate-200/60 shadow-xl shadow-slate-200/50 overflow-hidden relative flex flex-col">
                    {childrenWithProps}
                </div>
            </main>

            {/* The Vault Drawer (Global) */}
            <VaultDrawer
                isOpen={isVaultOpen}
                onClose={() => setIsVaultOpen(false)}
                user={user}
                episode={episode}
                db={db}
            />
        </div>
    );
};

export default StudioLayout;
